/**
 * Add-ons v2 — Independent optional accessories (multi-select).
 *
 * Responsibilities:
 * - Multi-select add-ons; no linkage with Complete Bundles / Build Your Own; does not rewrite main variant id
 * - On main ATC submit: if any checked, intercept and cart/add.js with
 *   "current main variant + selected add-ons"; if none checked, allow default ProductForm submit
 *
 * DOM: `<add-ons-v2 data-product-form-id>` + `.add-ons-v2__card[data-variant-id]`
 * Liquid: `add-ons-v2-card`; product.id === 7082140270661 shows Software badge
 *
 * Does not modify theme.js.liquid.
 */
if (!customElements.get('add-ons-v2')) {
  /** Set true to enable console debug logs */
  const DEBUG = false;

  class AddOnsV2 extends HTMLElement {
    connectedCallback() {
      this._onSubmit = this.onFormSubmit.bind(this);
      this.initSelection();
      this.bindFormSubmit();
    }

    disconnectedCallback() {
      this.getProductForm()?.removeEventListener('submit', this._onSubmit, true);
    }

    log(label, ...args) {
      if (!DEBUG) return;
      console.log(`[Add-ons v2 · ${label}]`, ...args);
    }

    getProductForm() {
      const productFormId = this.dataset.productFormId;

      if (!productFormId) {
        return null;
      }

      return this.ownerDocument.forms[productFormId] || null;
    }

    getCards() {
      return [...this.querySelectorAll('.add-ons-v2__card')];
    }

    getSelectedCards() {
      return this.getCards().filter((card) => card.classList.contains('is-selected'));
    }

    getRequestedQuantity() {
      const quantityInput = this.getProductForm()?.querySelector('input[name="quantity"]');
      const quantity = Number.parseInt(quantityInput?.value || '1', 10);

      return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    }

    /**
     * Read the main form variant id written by CB/BYO (or page default).
     * @returns {number|null}
     */
    getMainVariantId() {
      const productFormId = this.dataset.productFormId;

      if (!productFormId) {
        return null;
      }

      const input =
        this.ownerDocument.querySelector(`form#${productFormId} input[name="id"]`) ||
        this.ownerDocument.querySelector(`input[name="id"][form="${productFormId}"]`);

      const variantId = Number.parseInt(input?.value || '', 10);

      return Number.isFinite(variantId) ? variantId : null;
    }

    toggleCard(card) {
      const isSelected = card.classList.contains('is-selected');

      card.classList.toggle('is-selected', !isSelected);
      card.setAttribute('aria-pressed', String(!isSelected));

      this.log('toggle selection', {
        productId: card.dataset.productId,
        variantId: card.dataset.variantId,
        selected: !isSelected,
        selectedCount: this.getSelectedCards().length,
      });
    }

    /**
     * Capture-phase submit intercept: when add-ons are selected, add main + add-ons together.
     * @param {SubmitEvent} event
     */
    async onFormSubmit(event) {
      const selectedCards = this.getSelectedCards().filter(
        (card) => card.dataset.variantAvailable !== 'false'
      );

      // No add-ons selected → do not intercept; default ProductForm adds main only
      if (!selectedCards.length) {
        return;
      }

      const mainVariantId = this.getMainVariantId();

      if (!mainVariantId) {
        this.log('add skip', { reason: 'missing main variant id' });
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      const productForm = this.getProductForm();
      const submitButtons = productForm
        ? [...productForm.elements].filter((el) => el.type === 'submit')
        : [];

      submitButtons.forEach((button) => {
        button.setAttribute('disabled', 'disabled');
        button.setAttribute('aria-busy', 'true');
      });

      const quantity = this.getRequestedQuantity();
      const items = [
        { id: mainVariantId, quantity },
        ...selectedCards
          .map((card) => ({
            id: Number.parseInt(card.dataset.variantId || '', 10),
            quantity,
          }))
          .filter((item) => Number.isFinite(item.id)),
      ];

      let sectionsToBundle = ['variant-added'];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle },
        })
      );

      try {
        const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items,
            sections: sectionsToBundle.join(','),
            sections_url: window.location.pathname,
          }),
        });

        const responseJson = await response.json();

        if (!response.ok) {
          productForm?.dispatchEvent(
            new CustomEvent('cart:error', {
              bubbles: true,
              detail: {
                error: responseJson.description || responseJson.message || 'Error',
              },
            })
          );
          return;
        }

        if (
          window.themeVariables?.settings?.cartType === 'page' ||
          window.themeVariables?.settings?.pageType === 'cart'
        ) {
          window.location.href = `${Shopify.routes.root}cart`;
          return;
        }

        const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        cartContent.sections = responseJson.sections;

        productForm?.dispatchEvent(
          new CustomEvent('variant:add', {
            bubbles: true,
            detail: {
              items: responseJson.items || [responseJson],
              cart: cartContent,
            },
          })
        );

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', {
            bubbles: true,
            detail: { cart: cartContent },
          })
        );

        this.log('add success', { items });
      } catch (error) {
        this.log('add failed', error);
      } finally {
        submitButtons.forEach((button) => {
          button.removeAttribute('disabled');
          button.removeAttribute('aria-busy');
        });
      }
    }

    /** Bind in capture phase so we run before theme ProductForm submit handling */
    bindFormSubmit() {
      const productForm = this.getProductForm();

      if (!productForm) {
        return;
      }

      productForm.removeEventListener('submit', this._onSubmit, true);
      productForm.addEventListener('submit', this._onSubmit, true);
    }

    initSelection() {
      const cards = this.getCards();

      if (!cards.length) {
        this.hidden = true;
        return;
      }

      cards.forEach((card) => {
        card.addEventListener('click', () => this.toggleCard(card));
      });
    }
  }

  customElements.define('add-ons-v2', AddOnsV2);
}
